import React from 'react';
import { Star, Lock, TrendingUp, TrendingDown, Clock, HelpCircle, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export type ChipType = 'Filter' | 'Toggle' | 'WineType' | 'Maturity';
export type ChipState = 'Default' | 'Selected' | 'Disabled';
export type MaturityValue = 'hold' | 'drink-or-hold' | 'drink' | 'drink-soon' | 'past-peak' | 'unknown';

const maturityIcons: Record<MaturityValue, LucideIcon> = {
  'hold': Lock,
  'drink-or-hold': TrendingUp,
  'drink': Star,
  'drink-soon': Clock,
  'past-peak': TrendingDown,
  'unknown': HelpCircle,
};

export interface ChipProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: ChipType;
  state?: ChipState;
  label: string;
  indicatorColor?: string;
  maturityValue?: MaturityValue;
}

export const Chip: React.FC<ChipProps> = ({
  variant = 'Filter',
  state = 'Default',
  label,
  indicatorColor,
  maturityValue,
  className,
  ...props
}) => {
  const isSelected = state === 'Selected';
  const isDisabled = state === 'Disabled';
  const isMaturity = variant === 'Maturity';
  const hasIndicator = variant === 'WineType' || isMaturity;

  const baseStyles = cn(
    "inline-flex items-center justify-center transition-all duration-150 ease-out select-none rounded-full cursor-pointer uppercase tracking-wider font-[family-name:var(--rc-font-mono)]",
    isMaturity && isSelected && maturityValue && "gap-1.5"
  );

  const sizingStyles = cn(
    "h-auto",
    isMaturity ? "px-2 md:px-4 py-1.5 min-h-[24px]" : "px-4 py-1.5 min-h-[32px]"
  );

  let maturitySelectedStyles = "";
  if (isMaturity && isSelected && maturityValue) {
    const map: Record<MaturityValue, string> = {
      'hold': "bg-[var(--rc-maturity-hold-bg)] text-[var(--rc-maturity-hold-text)]",
      'drink-or-hold': "bg-[var(--rc-maturity-drink-or-hold-bg)] text-[var(--rc-maturity-drink-or-hold-text)] border border-[var(--rc-border-subtle)]",
      'drink': "bg-[var(--rc-maturity-drink-bg)] text-[var(--rc-maturity-drink-text)]",
      'drink-soon': "bg-[var(--rc-maturity-drink-soon-bg)] text-[var(--rc-maturity-drink-soon-text)]",
      'past-peak': "bg-[var(--rc-maturity-past-peak-bg)] text-[var(--rc-maturity-past-peak-text)]",
      'unknown': "bg-[var(--rc-maturity-unknown-bg)] text-[var(--rc-maturity-unknown-text)] border border-[var(--rc-border-subtle)]",
    };
    maturitySelectedStyles = map[maturityValue];
  }

  const variantStyles = cn(
    "text-[12px] font-bold",
    !isMaturity && "border",
    !isSelected && !isDisabled && "bg-[var(--rc-surface-elevated)] border-[var(--rc-border-subtle)] text-[var(--rc-ink-primary)] hover:bg-[var(--rc-surface-secondary)]",
    isSelected && !isMaturity && "bg-[var(--rc-selection-bg)] border-[var(--rc-selection-border)] text-[var(--rc-selection-text)]",
    isMaturity && isSelected && maturitySelectedStyles,
    isDisabled && "bg-[var(--rc-disabled-bg)] border-[var(--rc-disabled-border)] text-[var(--rc-disabled-text)] cursor-not-allowed"
  );

  return (
    <div
      className={cn(baseStyles, sizingStyles, variantStyles, className)}
      {...props}
    >
      {hasIndicator && !isMaturity && (
        <div className="mr-2 flex items-center justify-center w-2 h-2 rounded-full overflow-hidden">
          <div
            className={cn("w-full h-full rounded-full", isDisabled ? "bg-[var(--rc-disabled-border)]" : "")}
            style={!isDisabled ? { backgroundColor: indicatorColor || 'var(--rc-accent-pink)' } : {}}
          />
        </div>
      )}
      {isMaturity && isSelected && maturityValue && maturityIcons[maturityValue] && (() => {
        const Icon = maturityIcons[maturityValue];
        return <Icon size={14} strokeWidth={2} className="shrink-0" />;
      })()}
      <span className="leading-none">{label}</span>
    </div>
  );
};
